import { Component } from '@angular/core';
import { OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { AlertsService } from '../../services/alerts.service';
import { ProductService } from '../../services/product.service';
import { ViewportService } from '../../services/viewport.service';

import { CropperComponent } from 'angular-cropperjs';

/**
 * Provides a view of an image product.
 */
@Component({
	selector: 'doc-image-view',
	templateUrl: './doc-image-view.component.html',
	styleUrl: './doc-image-view.component.css'
})
export class DocImageViewComponent implements OnInit, OnChanges {

	@Input() product!: any;
	@Input() documentData!: { [key: string]: any };
	@Input() viewerURL: SafeResourceUrl | undefined;
	@Input() chippingEnabled: boolean = false;
	@Input() projectionEnabled: boolean = false;
	
	@Output() triggerProjectImage = new EventEmitter<{ pageNumber: number }>();

	@ViewChild('angularCropper') public angularCropper!: CropperComponent;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	isMobile: boolean = false;
	panelOn: boolean = false;

	cropperConfig: any;
	scaleValX: number = 1;
	scaleValY: number = 1;
	isCropBoxVisible: boolean = false;

	imageHeight: number = 0;
	imageWidth: number = 0;

	chipOn: boolean = false;
	supportedChipFormats: any[] = [];

	chip: {
		minX: number; maxX: number; minY: number; maxY: number;
		pxCoordinates: string; chipFormat: string; chipPackageType: string;
		chipRequestName: string; chipEmailAddress: string | null; chipEmailNotification: boolean;
	} = {
			minX: 0,
			maxX: 0,
			minY: 0,
			maxY: 0,
			pxCoordinates: '',
			chipFormat: '',
			chipPackageType: "PRODUCT",
			chipRequestName: "Image Chip",
			chipEmailAddress: null,
			chipEmailNotification: false
		};

	constructor(
		private userService: UserService,
		private configService: ConfigService,
		private alertsService: AlertsService,
		private productService: ProductService,
		private viewportService: ViewportService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();

		// Replacement for the viewportsizemonitor directive
		this.viewportService.isMobile$.subscribe(isMobile => {
			this.isMobile = isMobile;
		});
	}

	ngOnInit(): void {
		this.initializeCropper();
		this.loadDocument();
	}

	/**
	 * Reinitialize this component without destroying the DOM.
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (changes['viewerURL'] || changes['product']) {
			if (this.angularCropper?.cropper) {
				this.angularCropper.cropper.destroy();
			}
			this.initializeCropper();
			this.loadDocument();
		}
	}

	private loadDocument(): void {
		this.initChipping();
	}

	private initChipping(): void {
		this.chip.chipEmailAddress = this.userService.user.email;


		//		if (!this.pages || this.pages.length == 0) {
		this.productService.getChipFormats(this.product.id).then(
			(formats: any) => {
				this.supportedChipFormats = [];

				if (!Array.isArray(formats)) {
					formats = [formats];
				}

				for (let i = 0; i < formats.length; i++) {
					let format = formats[i].split("==");
					this.supportedChipFormats.push({
						display: format[1],
						value: format[0]
					});
				}

				if (this.supportedChipFormats.length > 0) {
					this.chip.chipFormat = this.supportedChipFormats[0].value;
				}
				if (this.supportedChipFormats.length > 0 && this.configService.getConfig().esri.chippingEnabled) {
					this.chipOn = true;
				} else {
					this.chipOn = false;
				}
			},
			(err: any) => {
				console.warn("Failed to get supported chip formats.");
			}
		);
		//		}
	}

	performChip(): void {
		if (this.chip.minX < 0) {
			this.chip.minX = 0;
		}
		if (this.chip.minY < 0) {
			this.chip.minY = 0;
		}
		if (this.chip.maxX > this.imageWidth) {
			this.chip.maxX = this.imageWidth;
		}
		if (this.chip.maxY > this.imageHeight) {
			this.chip.maxY = this.imageHeight;
		}

		if (this.chip.minX != 0 && this.chip.minY != 0 && this.chip.maxX != 0 && this.chip.maxY != 0) {
			let coords = [];
			coords.push(this.chip.minY);
			coords.push(this.chip.minX);
			coords.push(this.chip.maxY);
			coords.push(this.chip.maxX);
			coords.push(this.imageWidth);
			coords.push(this.imageHeight);
			this.chip.pxCoordinates = coords.join(",");
		}

		this.productService.doChip([this.product.id], this.chip).then(
			(response: any) => {
				this.alertsService.success({
					message: this.LOCALIZATION.chipRequestSuccess,
					delay: 4000
				});
			},
			(err: any) => {
				console.error("Failed to create chip request:", err);
				this.alertsService.error({
					message: this.LOCALIZATION.chipRequestError,
					delay: 4000
				});
			}
		);
	}

	toggleChipPanel(): void {
		this.panelOn = !this.panelOn;
		if (!this.panelOn) {
			this.clear();
			this.angularCropper.cropper.setDragMode('move');
			this.reset();
		}
	}

	// --------------------------------
	// Cropper image viewer functions
	// --------------------------------

	private initializeCropper() {
		// For options, see: https://github.com/fengyuanchen/cropperjs/blob/v1/README.md#options
		this.cropperConfig = {
			dragMode: 'move',
			aspectRatio: 1,
			viewMode: 1,
			movable: true,
			zoomable: true,
			scalable: true,
			rotatable: true,
			autoCrop: false,
			autoCropArea: 0.8,
			ready: this.getImageDimensions.bind(this),
			crop: this.getBoundingBox.bind(this)
		};
	}

	private getImageDimensions(event: any): void {
		const cropper = this.angularCropper?.cropper;
		if (cropper) {
			let imageData = cropper.getImageData();
			this.imageWidth = Math.round(imageData.naturalWidth);
			this.imageHeight = Math.round(imageData.naturalHeight);
			console.log(`Original image size: ${this.imageWidth}x${this.imageHeight}`);
		}
	}

	private getBoundingBox(event: any): void {
		const cropper = this.angularCropper?.cropper;
		if (cropper) {
			const data = cropper.getData(false); // true returns rounded values

			this.chip.minX = data.x;
			this.chip.minY = data.y;
			this.chip.maxX = data.x + data.width;
			this.chip.maxY = data.y + data.height;
		}
	}

	reset(): void {
		this.angularCropper.cropper.reset();
	}

	clear(): void {
		this.angularCropper.cropper.clear();
	}

	rotate(): void {
		this.angularCropper.cropper.rotate(90);
	}

	zoom(zoomIn: boolean): void {
		let factor = zoomIn ? 0.1 : -0.1;
		this.angularCropper.cropper.zoom(factor);
	}

	scaleX(): void {
		this.scaleValX = this.scaleValX * -1;
		this.angularCropper.cropper.scaleX(this.scaleValX);
	}

	scaleY(): void {
		this.scaleValY = this.scaleValY * -1;
		this.angularCropper.cropper.scaleY(this.scaleValY);
	}

	move(x: number, y: number): void {
		this.angularCropper.cropper.move(x, y);
	}

	toggleCropBox(): void {
		if (this.isCropBoxVisible) {
			this.angularCropper.cropper.clear();
			this.angularCropper.cropper.setDragMode('move');
		} else {
			this.angularCropper.cropper.setDragMode('crop');
			this.angularCropper.cropper.crop();
		}

		this.isCropBoxVisible = !this.isCropBoxVisible;
	}

	save(): void {
		// Result is stored as a Base64 image
		const croppedCanvas = this.angularCropper.cropper.getCroppedCanvas();
		const croppedImage = croppedCanvas.toDataURL(); // .toDataURL('image/jpeg', (100 / 100));
		console.log('Cropped image base64:', croppedImage);
	}
	
	projectImage(): void {
		let page = 0; // TODO do we need to pick up the current page (eg. NTF)?
		this.triggerProjectImage.emit({ pageNumber: page });
	}
}
